'use client';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import type { ClaireFailureGroupsReport } from '@borradh-workspace/api-client/types';
import { ExternalLink, RefreshCw, TriangleAlert } from 'lucide-react';
import {
  useClaireFailureGroups,
  useRefreshClaireFailureGroups,
} from '../../api/claire-failure-groups';
import { FailureGroupsTable } from './failure-groups-table';
import { FailureInbox } from './failure-inbox';

function Header({
  report,
  onRefresh,
  isRefreshing,
}: {
  report?: ClaireFailureGroupsReport;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div className="max-w-3xl">
        <h1 className="text-3xl font-bold">Chatbot failure groups</h1>
        <p className="text-muted-foreground">
          Every morning the replies the AI judges flagged go to the Claire
          triage queue in Langfuse, where a person marks each one as really
          Claire’s fault or not. The confirmed ones land here, to be sorted by
          cause, so each cause is fixed once and you can see whether it comes
          back.
        </p>
      </div>
      <div className="flex items-center gap-2">
        {report?.triage ? (
          <Button variant="outline" size="sm" asChild>
            <a href={report.triage.url} target="_blank" rel="noreferrer">
              Triage queue ({report.triage.pending} waiting)
              <ExternalLink />
            </a>
          </Button>
        ) : null}
        {onRefresh ? (
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={isRefreshing}
          >
            <RefreshCw className={cn(isRefreshing && 'animate-spin')} />
            Refresh
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function ClaireFailureGroupsView({
  report,
  onRefresh,
  isRefreshing,
}: {
  report: ClaireFailureGroupsReport;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}) {
  return (
    <>
      <Header
        report={report}
        onRefresh={onRefresh}
        isRefreshing={isRefreshing}
      />
      <div className="flex flex-col gap-6">
        {!report.langfuseConfigured ? (
          <Alert>
            <TriangleAlert />
            <AlertTitle>Langfuse is not connected on this API</AlertTitle>
            <AlertDescription>
              The confirmed replies are read from the production Langfuse
              project. Set CLAIRE_REGRESSION_LANGFUSE_PUBLIC_KEY and
              CLAIRE_REGRESSION_LANGFUSE_SECRET_KEY (or the LANGFUSE_* tracing
              keys in production) on the API. The groups below still work.
            </AlertDescription>
          </Alert>
        ) : report.inboxError ? (
          <Alert variant="destructive">
            <TriangleAlert />
            <AlertTitle>{report.inboxError}</AlertTitle>
            <AlertDescription>
              The groups below are up to date; try Refresh for the new confirmed
              replies.
            </AlertDescription>
          </Alert>
        ) : (
          <FailureInbox failures={report.inbox} groups={report.groups} />
        )}
        <FailureGroupsTable groups={report.groups} />
        <p className="text-muted-foreground text-xs">
          Confirmed replies read from Langfuse at{' '}
          {new Date(report.fetchedAt).toLocaleTimeString('en-GB')}; refreshed
          every few minutes. Labels from the last 90 days.
        </p>
      </div>
    </>
  );
}

export function ClaireFailureGroupsPage() {
  const { data, isLoading, isError, error } = useClaireFailureGroups();
  const { refresh, isRefreshing } = useRefreshClaireFailureGroups();

  return (
    <div className="container max-w-6xl py-8">
      {isLoading ? (
        <>
          <Header />
          <div className="flex flex-col gap-4">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
        </>
      ) : isError || !data ? (
        <>
          <Header />
          <Alert variant="destructive">
            <AlertTitle>Could not load the failure groups</AlertTitle>
            <AlertDescription>
              {error?.message ?? 'The request failed.'}
            </AlertDescription>
          </Alert>
        </>
      ) : (
        <ClaireFailureGroupsView
          report={data}
          onRefresh={() => refresh()}
          isRefreshing={isRefreshing}
        />
      )}
    </div>
  );
}
